
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Upload, Loader2, Camera } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ensureAvatarsBucketExists } from '@/lib/storage-helpers';

interface AvatarUploadProps {
  userId: string;
  url?: string | null;
  username?: string | null;
  onAvatarChange?: (url: string) => void;
  isReadOnly?: boolean;
  isUpdating?: boolean;
}

export function AvatarUpload({ 
  userId, 
  url, 
  username, 
  onAvatarChange,
  isReadOnly = false,
  isUpdating = false 
}: AvatarUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(url || null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // On mount, ensure storage bucket exists
  useEffect(() => {
    if (!isReadOnly) {
      ensureAvatarsBucketExists().catch(err => 
        console.error('Failed to check storage bucket:', err)
      );
    }
  }, [isReadOnly]);

  // Handle file upload
  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }
      
      const file = event.target.files[0];
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB.');
      }
      
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      
      if (!fileExt || !allowedExtensions.includes(fileExt)) {
        throw new Error('File must be an image (JPG, PNG, GIF, WEBP).');
      }
      
      // Ensure bucket exists before upload
      await ensureAvatarsBucketExists();
      
      // Create a unique filename with timestamp
      const timestamp = new Date().getTime();
      const filePath = `${userId}-${timestamp}.${fileExt}`;
      
      // Log the upload process for debugging
      console.log('Starting avatar upload:', { userId, filePath });
      
      // Upload the file to Supabase storage
      const { data, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });
      
      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }
      
      console.log('Upload successful:', data);
      
      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      
      console.log('Generated public URL:', urlData.publicUrl);
      
      // Update the user's avatar_url in the profiles table
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: urlData.publicUrl })
        .eq('id', userId);
      
      if (updateError) {
        console.error('Profile update error:', updateError);
        throw updateError;
      }
      
      // Update local state
      setAvatarUrl(urlData.publicUrl);
      
      // Call the callback if provided
      if (onAvatarChange) {
        onAvatarChange(urlData.publicUrl);
      }
      
      // Invalidate profile queries to refetch with new avatar
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['profile', userId] });
      queryClient.invalidateQueries({ queryKey: ['session'] });
      
      toast({
        title: "Success",
        description: "Avatar updated successfully!",
      });
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error.message || "Could not upload avatar",
      });
    } finally {
      setUploading(false);
      
      // Clear the file input
      const fileInput = document.getElementById('avatar-upload') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };
  
  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="relative">
        <Avatar className="h-28 w-28 ring-4 ring-primary/20 transition-all duration-300 hover:ring-primary/40">
          <AvatarImage 
            src={avatarUrl || ''} 
            alt={username || 'User'} 
            className="object-cover"
          />
          <AvatarFallback className="bg-primary/10 text-primary text-2xl">
            {(username || 'U')[0].toUpperCase()}
          </AvatarFallback>
        </Avatar>
        
        {!isReadOnly && (
          <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
            <Button
              variant="secondary" 
              size="sm"
              className="rounded-full shadow-md flex items-center gap-2 px-4 bg-primary text-white hover:bg-primary/90"
              disabled={uploading || isUpdating}
              onClick={() => {
                const fileInput = document.getElementById('avatar-upload') as HTMLInputElement;
                if (fileInput) fileInput.click();
              }}
            >
              {uploading || isUpdating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Camera className="h-4 w-4" />
              )}
              <span>Upload Photo</span>
            </Button>
          </div>
        )}
      </div>
      
      {!isReadOnly && (
        <input
          id="avatar-upload"
          type="file"
          accept="image/*"
          onChange={uploadAvatar}
          disabled={uploading || isUpdating}
          className="hidden"
        />
      )}
    </div>
  );
}
